import type {
    ReactNode,
    MouseEventHandler,
    MouseEvent,
    FocusEventHandler,
    FocusEvent,
} from 'react';
import React, { forwardRef, useMemo } from 'react';
import type { RouterLocation } from '@funtech-int/rival';
import { equalsLocation } from '@funtech-int/rival';
import { useRouteMatch, useRouter } from '@funtech-int/rival-react';

interface RouterLinkProps {
    title?: string;
    className?: string;
    activeClassName?: string;
    replace?: boolean;
    children?: ReactNode;
    to: RouterLocation<any, any>;
    searchParams?: URLSearchParams;
    dataTid?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    onFocus?: FocusEventHandler<HTMLAnchorElement>;
    onBlur?: FocusEventHandler<HTMLAnchorElement>;
}

export const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps>((props, ref) => {
    const location = useRouteMatch();
    const router = useRouter();

    const memoized = useMemo(
        () => ({
            href:
                router.serialize(props.to) +
                (props.searchParams?.size ? `?${props.searchParams.toString()}` : ''),

            onClick(event: MouseEvent<HTMLAnchorElement>) {
                if (props.onClick) {
                    props.onClick(event);
                }

                if (!event.defaultPrevented) {
                    const meta = props.searchParams
                        ? { searchParams: props.searchParams }
                        : undefined;

                    router.navigate({
                        to: props.to,
                        replace: props.replace,
                        meta,
                    });
                }

                event.preventDefault();
            },
            onFocus(event: FocusEvent<HTMLAnchorElement>) {
                if (props.onFocus) {
                    props.onFocus(event);
                }
            },
            onBlur(event: FocusEvent<HTMLAnchorElement>) {
                if (props.onBlur) {
                    props.onBlur(event);
                }
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [router, props.to, props.replace, props.onClick]
    );

    const className = [
        props.className,
        equalsLocation(location, props.to) ? props.activeClassName : undefined,
    ].join(' ');

    return (
        <a
            ref={ref}
            className={className}
            title={props.title}
            href={memoized.href}
            onClick={memoized.onClick}
            onFocus={memoized.onFocus}
            onBlur={memoized.onBlur}
            data-tid={props.dataTid ?? 'RouterLink'}
        >
            {props.children}
        </a>
    );
});
